'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';

interface QuoteFormData {
  serviceType: string;
  origin: string;
  destination: string;
  weight: string;
  dimensions: string;
  shipmentType: string;
}

export default function QuoteCalculator() {
  const [formData, setFormData] = useState<QuoteFormData>({
    serviceType: '',
    origin: '',
    destination: '',
    weight: '',
    dimensions: '',
    shipmentType: 'package',
  });
  const [calculatedQuote, setCalculatedQuote] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setShowResult(false);
  };

  const calculateQuote = (e: React.FormEvent) => {
    e.preventDefault();

    // Simple pricing calculation logic
    let basePrice = 0;
    const weight = parseFloat(formData.weight) || 0;

    switch (formData.serviceType) {
      case 'express':
        basePrice = 25 + weight * 2.5;
        break;
      case 'freight':
        basePrice = 150 + weight * 0.8;
        break;
      case 'warehousing':
        basePrice = weight * 0.5 * 30; // Monthly rate
        break;
      case 'customs':
        basePrice = 75 + weight * 1.2;
        break;
      default:
        basePrice = 50;
    }

    // Add shipment type multiplier
    if (formData.shipmentType === 'pallet') {
      basePrice *= 1.5;
    } else if (formData.shipmentType === 'container') {
      basePrice *= 3;
    }

    const finalQuote = Math.round(basePrice * 100) / 100;
    setCalculatedQuote(finalQuote);
    setShowResult(true);

    // Track analytics
    analytics.trackQuoteRequest({
      name: 'Quote Calculator',
      email: 'calculator@shiptrack.com',
      subject: `${formData.serviceType} - ${formData.origin} to ${formData.destination}`,
    });
    analytics.trackConversion('quote_calculated', finalQuote);
  };

  const resetCalculator = () => {
    setFormData({
      serviceType: '',
      origin: '',
      destination: '',
      weight: '',
      dimensions: '',
      shipmentType: 'package',
    });
    setCalculatedQuote(null);
    setShowResult(false);
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Quote Calculator
          </h2>
          <p className="text-lg text-muted-foreground">
            Get an instant estimate for your shipment costs
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-card rounded-xl shadow-card border border-border p-8">
          {!showResult ? (
            <form onSubmit={calculateQuote} className="space-y-6">
              {/* Service Type */}
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-foreground mb-2">
                  Service Type *
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  required
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                >
                  <option value="">Select a service</option>
                  <option value="express">Express Shipping</option>
                  <option value="freight">Freight Services</option>
                  <option value="warehousing">Warehousing</option>
                  <option value="customs">Customs Clearance</option>
                </select>
              </div>

              {/* Origin & Destination */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="origin" className="block text-sm font-medium text-foreground mb-2">
                    Origin *
                  </label>
                  <input
                    id="origin"
                    name="origin"
                    type="text"
                    required
                    value={formData.origin}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="City, Country"
                  />
                </div>
                <div>
                  <label htmlFor="destination" className="block text-sm font-medium text-foreground mb-2">
                    Destination *
                  </label>
                  <input
                    id="destination"
                    name="destination"
                    type="text"
                    required
                    value={formData.destination}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="City, Country"
                  />
                </div>
              </div>

              {/* Weight & Dimensions */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="weight" className="block text-sm font-medium text-foreground mb-2">
                    Weight (lbs) *
                  </label>
                  <input
                    id="weight"
                    name="weight"
                    type="number"
                    required
                    min="0"
                    step="0.1"
                    value={formData.weight}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label htmlFor="dimensions" className="block text-sm font-medium text-foreground mb-2">
                    Dimensions (L x W x H)
                  </label>
                  <input
                    id="dimensions"
                    name="dimensions"
                    type="text"
                    value={formData.dimensions}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="12 x 8 x 6 inches"
                  />
                </div>
              </div>

              {/* Shipment Type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Shipment Type *</label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'package', label: 'Package', icon: 'CubeIcon' },
                    { value: 'pallet', label: 'Pallet', icon: 'Square3Stack3DIcon' },
                    { value: 'container', label: 'Container', icon: 'ArchiveBoxIcon' },
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, shipmentType: type.value })}
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all ${
                        formData.shipmentType === type.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon name={type.icon as any} size={24} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <Icon name="CalculatorIcon" size={20} />
                <span>Calculate Quote</span>
              </button>
            </form>
          ) : (
            <div className="text-center space-y-6">
              {/* Quote Result */}
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-8 border border-border">
                <Icon name="CheckCircleIcon" size={48} className="text-success mx-auto mb-4" />
                <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">Estimated Quote</h3>
                <div className="flex items-baseline justify-center gap-2 mb-4">
                  <span className="text-5xl font-bold text-primary">${calculatedQuote}</span>
                  <span className="text-lg text-muted-foreground">USD</span>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    <strong>Service:</strong> {formData.serviceType.charAt(0).toUpperCase() + formData.serviceType.slice(1)}
                  </p>
                  <p>
                    <strong>Route:</strong> {formData.origin} → {formData.destination}
                  </p>
                  <p>
                    <strong>Weight:</strong> {formData.weight} lbs
                  </p>
                  <p>
                    <strong>Type:</strong> {formData.shipmentType.charAt(0).toUpperCase() + formData.shipmentType.slice(1)}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={resetCalculator}
                  className="flex-1 px-6 py-3 border border-border rounded-lg font-semibold hover:bg-muted transition-all"
                >
                  Calculate Another
                </button>
                <button
                  onClick={() => {
                    analytics.trackConversion('quote_to_contact');
                    window.location.href = '/contact';
                  }}
                  className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105"
                >
                  Request Full Quote
                </button>
              </div>

              <p className="text-xs text-muted-foreground">
                * This is an estimated quote. Final pricing may vary based on additional factors.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}